import { AttendeeRole, IAttendeeInput, IAttendeeOrganizationDTO, IControlAttendeeDetailsDTO } from './attendees.dao';
import { IOperatorDetailsDTO, IRoomDetailsDTO } from './rooms.dao';
import {
    IEventRequestParams,
    IPaginationParams,
    ISortFieldsRequestParams,
    ISortRequestParams,
    IInfinityScrollPaginationParams
} from './shared.dto';
import { IConnectionDetailsDTO, IConnectionGroupDTO } from './connections.dao';
import { IMeetingRequestParams, IStartDateTimesDTO, MeetingStatus } from './meetings.dao';
import { ConnectionVersion } from '../constants/common.constants';
import { IIgnoreAttendeesRequest } from './organizations.dao';

abstract class IControlDAO {
    public abstract getControlEventMeetingsList(params: IGetControlMeetingsRequestParams, signal?: AbortSignal): Promise<IControlEventMeetingsDTO>;
    public abstract getControlEventMeeting(params: IControlMeetingRequestParams): Promise<IControlMeetingDetailsDTO>;
    public abstract updateControlEventMeeting(
        params: IControlMeetingRequestParams & IUpdateMeetingInput
    ): Promise<IControlMeetingDetailsDTO>;
    public abstract getControlEventMeetingsCSV(params: IEventRequestParams & IStartDateTimesDTO): Promise<void>;
    public abstract getMeetingStartHours(params: IEventRequestParams): Promise<Date[]>;
    public abstract getControlAttendee(
        params: IEventRequestParams & { attendeeId: number }
    ): Promise<IControlAttendeeDetailsDTO>;
    public abstract getMeetingTypes(params: IEventRequestParams): Promise<string[]>;
    public abstract getAttendeeLiveSessions(params: IAttendeeLiveSessionParams): Promise<IAttendeeLiveSessionResponse>;
    public abstract getMeetingRegistrants(params: IMeetingRequestParams): Promise<IRegistrantsListResponse>;
}

enum MeetingProgress {
    STANDBY = 'STANDBY',
    READY = 'READY',
    UNDERWAY = 'UNDERWAY',
    ENDED = 'ENDED'
}

type TGetMeetingsSortKeys = 'startDateTime' | 'endDateTime' | 'status' | 'organizations.role' | 'organizations.organization.name';

type IGetControlMeetingsRequestParams =
    IEventRequestParams &
    (ISortFieldsRequestParams<TGetMeetingsSortKeys> | ISortRequestParams<TGetMeetingsSortKeys>) &
    IControlMeetingFilterParams &
    IPaginationParams &
    IIgnoreAttendeesRequest
    ;

interface IControlMeetingRequestParams extends IEventRequestParams, IIgnoreAttendeesRequest {
    meetingId: number;
    status?: string;
}

interface IControlMeetingFilterParams {
    startDateTime?: string;
    operatorIds?: string;
    types?: string;
    endDateTime?: string;
}

interface IControlEventMeetingsDTO {
    content: IControlMeetingDetailsDTO[];
    totalElements: number;
}

export enum OperatorStatus {
    READY = 'READY',
    NOT_READY = 'NOT_READY'
}

export enum CONTROL_FILTER_VIEW_TAB {
    CONTROL = 'CONTROL_TAB'
}

export enum AttendeeStatus {
    ACTIVE = 'Active',
    TOTAL = 'Total',
    DEPARTURE = 'Departure'
}

interface IRoomInput {
    roomOperator?: IRoomDetailsDTO;
}

interface IControlRoomDTO extends IRoomInput {
    roomId: number;
    eventId?: number;
    roomName: string;
    roomLabel: string;
    operators: IOperatorDetailsDTO[];
    defaultConnection?: IConnectionDetailsDTO;
    presentersConnection?: IConnectionDetailsDTO;
    hostsConnection?: IConnectionDetailsDTO;
    connectionGroups: IConnectionGroupDTO[];
    connections: IConnectionDetailsDTO[];
}

interface IMeetingAttendeeInput {
    organizationId: string;
    role: AttendeeRole;
    attendeeIds?: number[];
}

interface IUpdateMeetingInput extends IControlMeetingRequestParams {
    attendee?: IAttendeeInput;
    addMeetingAttendees?: IMeetingAttendeeInput[];
    progress: MeetingProgress;
    operatorMeetingNotes: string;
}

interface IControlMeetingDetailsDTO {
    topic: string;
    meetingId: number;
    progress: MeetingProgress;
    status: MeetingStatus;
    presenters: IAttendeeOrganizationDTO[];
    room?: IControlRoomDTO;
    startDateTime: Date;
    endDateTime: Date;
    hosts: IAttendeeOrganizationDTO[];
    participants: IAttendeeOrganizationDTO[];
    registrants: IAttendeeOrganizationDTO[];
    supports: IAttendeeOrganizationDTO[];
    operatorMeetingNotes: string;
    type?: string;
    connections: IConnectionDetailsDTO[];
    connectionGroups: IConnectionGroupDTO[];
    defaultConnectionId?: number;
    presenterRoleConnectionId?: number | null;
    hostRoleConnectionId?: number | null;
    supportRoleConnectionId?: number | null;
    meetingConnectionNotes: string;
    meetingRecordEnabled?: boolean;
    operatorSharedScreen?: boolean | null;
    operators: IOperatorDetailsDTO[];
    total?: number;
    connectionVersion?: ConnectionVersion;
    registrantsCount?: number;
    podiumLinkEnabled: boolean;
}

interface IUpdateMeetingInput {
    attendees?: IAttendeeInput[];
    addMeetingAttendees?: IMeetingAttendeeInput[];
    progress: MeetingProgress;
    operatorMeetingNotes: string;
    status: MeetingStatus;
    requestedTime?: string;
    removeMeetingAttendees?: IMeetingAttendeeInput[];
    meetingOperators?: IOperatorDetailsDTO[];
    meetingConnections?: IConnectionDetailsDTO[];
    operatorSharedScreen?: boolean | null;
}

interface IUpdateMeetingSubscriptionResponse {
    value: {
        data: {
            onUpdateMeetingControl: IControlMeetingDetailsDTO & { requestedTime?: string };
        };
    };
}

interface IUpdateRoomSubscriptionResponse {
    value: {
        data: {
            onUpdateRoom: IControlRoomDTO & { requestedTime?: string };
        };
    };
}

interface IMeetingAttendeeLiveData {
    meetingId: number;
    total: number;
}

interface IAttendeeLiveSessionParams extends IControlMeetingRequestParams, IInfinityScrollPaginationParams {
    status: string;
}

interface IUpdateMeetingAttendeeLiveDataResponse {
    value: {
        data: {
            onPopulateAggregatedAttendeeLiveCounters: IMeetingAttendeeLiveData;
        };
    };
}

interface IAttendeeLiveSessionResponse {
    content: IAttendeeLiveSessionDTO[];
    totalCount: number;
    nextPageToken?: string;
}

interface IAttendeeLiveSessionDTO {
    id: string;
    connectionId: number;
    meetingId: number;
    attendeeEmail: string;
    attendeeFirstLastName: string;
    joinTime: Date;
    leftTime: Date;
    meetingConnectionType: string;
    organizationName: string;
}

interface IRegistrantsListResponse {
    content: IRegistrantDTO[];
    totalElements: number;
}

interface IRegistrantDTO {
    attendeeId: number;
    status: string;
    name: string;
    organizationName: string;
}

type TCopyPodiumLinkFrom = 'Control tab'
    | 'Run Meeting Small'
    | 'Run Meeting Large'
;

export {
    IControlDAO,
    IControlEventMeetingsDTO,
    IControlMeetingDetailsDTO,
    IControlMeetingRequestParams,
    MeetingProgress,
    IControlRoomDTO,
    TGetMeetingsSortKeys,
    IMeetingAttendeeInput,
    IUpdateMeetingInput,
    IUpdateMeetingSubscriptionResponse,
    IUpdateRoomSubscriptionResponse,
    IGetControlMeetingsRequestParams,
    IMeetingAttendeeLiveData,
    IUpdateMeetingAttendeeLiveDataResponse,
    IAttendeeLiveSessionResponse,
    IAttendeeLiveSessionDTO,
    IAttendeeLiveSessionParams,
    IRegistrantDTO,
    IRegistrantsListResponse,
    TCopyPodiumLinkFrom,
};